// Import Libraries
import React, { useContext, useEffect, useState } from 'react';
import {
  Button,
  ButtonGroup,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { toast } from 'react-toastify';

// Import Components
import ExportMenuButton from './ExportMenuButton';
import ProjectSettingsModal from '../../Modals/project/projectSettings.js';
import UpgradeMembership from '../../Modals/Subscription/upgradeMembership.js';

// Context
import ScriptContext from '../../../context/Script';
import { UserContext } from '../../../context/User';
import ProjectViewContext from '../../../context/ProjectView';

// Import Services
import scriptService from '../../../services/scriptService';
import shotService from '../../../services/shotService';
import featuresService from '../../../services/features';
import analyticsService from '../../../services/analyticsService';
import useScenePermissions from '../../../hooks/useScenePermissions';
import useExpandAllShots from '../../../hooks/useExpandAllShots';

const GENERATE_TOOLTIP = (
  <ul style={{ margin: '0 8px', padding: '0 4px', listStyle: 'disc' }}>
    <li>Click the button to generate images for the entire scene in one go</li>
    <li>Delete the images you don't like and click again to regenerate</li>
    <li>If you upload your own images first, we'll use them as a reference</li>
  </ul>
);

// Construct Component
// Right half of the panel-tools row: Shots expand/collapse, Images switch,
// Generate images (only while Images is on) and Export.
const ShotsToolbar = ({ showExport = true }) => {
  const theme = useTheme();
  // iPad landscape shortens the Generate label; iPad portrait goes icon-only.
  const isCompact = useMediaQuery(theme.breakpoints.down('lg'));
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));

  const { user } = useContext(UserContext);
  const { script, setScript } = useContext(ScriptContext);
  const { view, setView } = useContext(ProjectViewContext);
  const expandAll = useExpandAllShots();

  const [isGeneratingImages, setIsGeneratingImages] = useState(false);
  const [showGenerateImages, setShowGenerateImages] = useState(false);
  const [refreshSceneId, setRefreshSceneId] = useState(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  const currentScene = view.sceneIndex !== null ? script.scenes?.[view.sceneIndex] : null;
  const { isSceneLocked } = useScenePermissions(currentScene, script);
  const currentShotList = currentScene?.shot_list ?? [];
  const hasShots = currentShotList.length > 0;
  const allShotsHaveImages = hasShots && currentShotList.every((s) => s.image);
  // Generation now returns from the server almost immediately and streams images in
  // over the socket as each one finishes, so isGeneratingImages alone (true only for
  // that brief initial request) is too short-lived to keep the button/label accurate
  // for the whole job. Fall back to the per-shot loading flags driven by those sockets.
  const isSceneGeneratingImages =
    isGeneratingImages || currentShotList.some((s) => s.imageIsLoading);

  useEffect(() => {
    async function checkToggle() {
      setShowGenerateImages(await featuresService.isFeatureEnabled('GenerateImagesButton'));
    }
    checkToggle();
  }, [script.id, user.id]);

  function includeImages(include) {
    if (view.sceneIndex === null) return;
    if (!hasShots) return;

    view.includeImages = include;
    setView((prevView) => {
      return {
        ...prevView,
        includeImages: include,
      };
    });
  }

  const generateReferenceImages = async (settings) => {
    if (isGeneratingImages) return;

    if (!script.project_settings && !settings) {
      setView((prevView) => ({
        ...prevView,
        projectSettingsOpen: true,
      }));
      return;
    }
    setIsGeneratingImages(true);

    try {
      if (view.sceneIndex === null) return;
      includeImages(true);
      if (
        !script.scenes[view.sceneIndex].shot_list ||
        script.scenes[view.sceneIndex].shot_list.length === 0
      )
        return;
      const scene = script.scenes[view.sceneIndex];
      await scriptService.generateReferenceImages(script.id, scene.id);
      setRefreshSceneId(scene.id);
    } catch (error) {
      console.error('Error generating images:', error);
      toast.error("Image generation didn't start. Please try again.");

      // The request never reached the backend (e.g. a dropped network connection), so no
      // socket event will ever come in to mark these shots as failed. Flag them locally so
      // the existing per-shot error/Retry UI (driven by imageGenerationFailed) shows up,
      // instead of leaving shots stuck looking like nothing happened.
      const sceneId = view.sceneIndex !== null ? script.scenes[view.sceneIndex]?.id : null;
      if (sceneId != null) {
        setScript((prevScript) => {
          const idx = prevScript.scenes.findIndex((s) => s.id === sceneId);
          if (idx === -1) return prevScript;
          const prevScene = prevScript.scenes[idx];
          const updatedShotList = (prevScene.shot_list || []).map((shot) =>
            shot.image ? shot : { ...shot, imageIsLoading: false, imageGenerationFailed: true },
          );
          const updatedScenes = [...prevScript.scenes];
          updatedScenes[idx] = { ...prevScene, shot_list: updatedShotList };
          return { ...prevScript, scenes: updatedScenes };
        });
      }
    } finally {
      setIsGeneratingImages(false);
    }
  };

  // Fallback: after the generate API call returns, fetch any images not yet received via socket.
  // Handles cases where socket events were dropped or images already existed on the backend.
  useEffect(() => {
    if (!refreshSceneId) return;
    const sceneId = refreshSceneId;
    setRefreshSceneId(null);

    const scene = script.scenes.find((s) => s.id === sceneId);
    const shotsNeedingImages = (scene?.shot_list ?? []).filter((s) => !s.image);
    if (!shotsNeedingImages.length) return;

    const fetchMissingImages = async () => {
      const results = await Promise.all(
        shotsNeedingImages.map(async (shot) => ({
          id: shot.id,
          image: await shotService.retrieveImg(shot.id),
        }))
      );
      const imageMap = Object.fromEntries(
        results.filter((r) => r.image).map((r) => [r.id, r.image])
      );
      if (!Object.keys(imageMap).length) return;

      setScript((prevScript) => {
        const idx = prevScript.scenes.findIndex((s) => s.id === sceneId);
        if (idx === -1) return prevScript;
        const prevScene = prevScript.scenes[idx];
        const updatedShotList = prevScene.shot_list.map((shot) =>
          imageMap[shot.id] && !shot.image
            ? { ...shot, image: imageMap[shot.id], imageIsLoading: false }
            : shot
        );
        const updatedScenes = [...prevScript.scenes];
        updatedScenes[idx] = { ...prevScene, shot_list: updatedShotList };
        return { ...prevScript, scenes: updatedScenes };
      });
    };

    fetchMissingImages();
  }, [refreshSceneId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerateClick = () => {
    if (isSceneLocked) {
      analyticsService.trackEvent('locked_scene_edit_attempted');
      analyticsService.trackEvent('upgrade_cta_clicked', { source: 'image_lock' });
      setUpgradeModalOpen(true);
      return;
    }
    generateReferenceImages();
  };

  const expanded = !!view.expandShots;
  const generateLabel = isSceneGeneratingImages
    ? 'Generating…'
    : isCompact
      ? 'Generate'
      : 'Generate images';

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={isNarrow ? 0.5 : 1}
      useFlexGap
      sx={{ minWidth: 0 }}
    >
      <Tooltip title={expanded ? 'Collapse all shot cards' : 'Expand all shot cards'}>
        <span>
          <Button
            color="inherit"
            size={isNarrow ? 'small' : 'medium'}
            aria-pressed={expanded}
            disabled={!hasShots}
            onClick={() => expandAll(!expanded)}
            endIcon={
              expanded ? <UnfoldLessIcon color="primary" /> : <UnfoldMoreIcon color="action" />
            }
          >
            Shots
            <Chip component="span" label={currentShotList.length} sx={{ ml: 1 }} />
          </Button>
        </span>
      </Tooltip>

      <FormControlLabel
        label="Images"
        disabled={!hasShots}
        control={
          <Switch
            checked={!!view.includeImages}
            onChange={(e) => includeImages(e.target.checked)}
          />
        }
      />

      {showGenerateImages && view.includeImages && (
        <ButtonGroup
          variant="outlined"
          size={isNarrow ? 'small' : 'medium'}
          aria-label="Image generation"
        >
          <Tooltip
            title={
              isSceneLocked
                ? 'Locked — upgrade to Pro to generate images for this scene.'
                : GENERATE_TOOLTIP
            }
          >
            <span>
              <Button
                onClick={handleGenerateClick}
                disabled={!isSceneLocked && (isSceneGeneratingImages || allShotsHaveImages)}
                aria-label={isNarrow ? 'Generate images' : undefined}
                startIcon={
                  isNarrow ? undefined : isSceneLocked ? (
                    <WorkspacePremiumIcon />
                  ) : (
                    <AutoAwesomeOutlinedIcon />
                  )
                }
              >
                {isNarrow ? (
                  isSceneLocked ? (
                    <WorkspacePremiumIcon fontSize="small" />
                  ) : (
                    <AutoAwesomeOutlinedIcon fontSize="small" />
                  )
                ) : (
                  generateLabel
                )}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Image generation settings">
            <Button
              aria-label="Image generation settings"
              onClick={() => setView((prev) => ({ ...prev, projectSettingsOpen: true }))}
            >
              <TuneIcon fontSize="small" />
            </Button>
          </Tooltip>
        </ButtonGroup>
      )}

      {showExport && (
        <Stack direction="row" sx={{ ml: 'auto' }}>
          <ExportMenuButton iconOnly={isNarrow} />
        </Stack>
      )}

      <ProjectSettingsModal
        open={view.projectSettingsOpen}
        onClose={() => setView((prev) => ({ ...prev, projectSettingsOpen: false }))}
        initialSettings={script.project_settings}
        onSaved={(settings) => {
          setScript((prevScript) => ({ ...prevScript, project_settings: settings }));
          generateReferenceImages(settings);
        }}
      />

      <UpgradeMembership
        isOpen={upgradeModalOpen}
        onRequestClose={() => setUpgradeModalOpen(false)}
        featureMsg="Continue shotlisting fast with Pro features"
      />
    </Stack>
  );
};

export default ShotsToolbar;
